import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { acceptInvitation } from '../services/teamAuthService';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f1419 0%, #1a1f2e 50%, #0f1419 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: rgba(26, 31, 46, 0.95);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 20px;
  padding: 48px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const LogoText = styled.h1`
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 14px;
  text-align: center;
  margin-bottom: 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #94a3b8;
  font-size: 13px;
  font-weight: 500;
`;

const Input = styled.input`
  background: #252b3d;
  border: 1px solid #3a4055;
  border-radius: 10px;
  padding: 14px 16px;
  color: #fff;
  font-size: 15px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
  }

  &::placeholder {
    color: #64748b;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(139, 92, 246, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  color: #f87171;
  font-size: 14px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  color: #4ade80;
  font-size: 14px;
  text-align: center;
`;

const PasswordRequirements = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #64748b;
  font-size: 12px;
  list-style: disc;

  li {
    margin-bottom: 4px;
  }

  li.valid {
    color: #4ade80;
  }
`;

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Please check your email for the correct link.');
    }
  }, [token]);

  const validatePassword = (pwd) => {
    return {
      length: pwd.length >= 8,
      hasNumber: /\d/.test(pwd),
      hasLetter: /[a-zA-Z]/.test(pwd)
    };
  };

  const passwordChecks = validatePassword(password);
  const isPasswordValid = passwordChecks.length && passwordChecks.hasNumber && passwordChecks.hasLetter;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid) {
      setError('Please create a password that meets all requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const result = await acceptInvitation(token, password);
      
      if (result.success) {
        setSuccess(true);
        // Redirect to main app after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(result.error || 'Failed to accept invitation');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <PageContainer>
        <Card>
          <Logo>
            <LogoText>iMaven</LogoText>
          </Logo>
          <SuccessMessage style={{ marginBottom: '24px' }}>
            🎉 Account activated successfully!
          </SuccessMessage>
          
          <Title style={{ fontSize: '18px', marginBottom: '16px' }}>What's next?</Title>
          
          <div style={{ 
            background: 'rgba(139, 92, 246, 0.1)', 
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '10px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <div style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.6' }}>
              <p style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#fff' }}>Step 1:</strong> Ask your company admin to share the login page
              </p>
              <p style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#fff' }}>Step 2:</strong> Login with your company's Auth0 account
              </p>
              <p>
                <strong style={{ color: '#fff' }}>Step 3:</strong> Enter your email and the password you just created
              </p>
            </div>
          </div>
          
          <p style={{ color: '#64748b', fontSize: '13px', textAlign: 'center' }}>
            You can now close this window.
          </p>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card>
        <Logo>
          <LogoText>iMaven</LogoText>
        </Logo>
        
        <Title>Set Up Your Account</Title>
        <Subtitle>Create a password to activate your team account</Subtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Password</Label>
            <Input
              type="password"
              placeholder="Create a secure password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={!token || loading}
            />
            <PasswordRequirements>
              <li className={passwordChecks.length ? 'valid' : ''}>At least 8 characters</li>
              <li className={passwordChecks.hasLetter ? 'valid' : ''}>At least one letter</li>
              <li className={passwordChecks.hasNumber ? 'valid' : ''}>At least one number</li>
            </PasswordRequirements>
          </FormGroup>

          <FormGroup>
            <Label>Confirm Password</Label>
            <Input
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={!token || loading}
            />
          </FormGroup>

          <Button 
            type="submit" 
            disabled={!token || loading || !isPasswordValid || !passwordsMatch}
          >
            {loading ? 'Setting up...' : 'Activate Account'}
          </Button>
        </Form>
      </Card>
    </PageContainer>
  );
}
